/**
 * Знаки наград: бонусы и купоны по видам услуг.
 *
 * Рисуются вектором, а не картинками и не эмодзи: знак меняет цвет вместе с
 * темой, остаётся резким на любом экране и одинаково выглядит на всех платформах —
 * эмодзи на айфоне и на андроиде разные, а тут нужен один знак.
 *
 * Каждый знак показывает предмет, а не абстракцию: лист с текстом — документы,
 * снимок с рамкой снизу — полароид, фотография с горизонтом — фотопечать. По
 * форме понятно, что получишь, без подписи.
 */

export type BonusKind = "points" | "coupon" | "document_print" | "photo_print" | "polaroid_print";

/** Как называется услуга на экране. */
export const BONUS_TITLES: Record<string, string> = {
  points: "Бонусы",
  coupon: "Купоны",
  document_print: "Печать документов",
  photo_print: "Печать фотографий",
  inkjet_print: "Печать фотографий",
  polaroid_print: "Полароидные фото",
  photo_id: "Фото на документы",
  scan: "Сканирование",
  copy: "Копирование",
};

/** Единица измерения награды: бонусы в рублях, купоны в единицах услуги. */
export const BONUS_UNITS: Record<string, [string, string, string]> = {
  document_print: ["лист", "листа", "листов"],
  photo_print: ["фотография", "фотографии", "фотографий"],
  inkjet_print: ["фотография", "фотографии", "фотографий"],
  polaroid_print: ["снимок", "снимка", "снимков"],
  photo_id: ["фото", "фото", "фото"],
  scan: ["скан", "скана", "сканов"],
  copy: ["копия", "копии", "копий"],
};

interface IconProps {
  className?: string;
}

/**
 * Бонусы: монета со звездой. Круг — ценность, которую копят и которой платят;
 * звезда внутри — награда. Без знака валюты: бонусы не деньги, хотя и гасят
 * цену рубль за рубль. Плюс здесь не годился — рядом с числом он читался как
 * кнопка «добавить». Звезда залита, а не обведена: на 18 px обводка слипается.
 */
export function PointsIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
      <circle cx="20" cy="20" r="14.5" stroke="currentColor" strokeWidth="2.2" />
      <path
        d="M20 11.7L22.29 16.84L27.89 17.44L23.71 21.21L24.88 26.71L20 23.9L15.12 26.71L16.29 21.21L12.11 17.44L17.71 16.84Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Купон без привязки к услуге: билет с надрезами по бокам и линией отрыва.
 * Нужен там, где купоны показываются одной строкой, а не по видам услуг.
 */
export function CouponIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
      <path
        d="M7.5 11.5H32.5V16.5C30.57 16.5 29 18.07 29 20C29 21.93 30.57 23.5 32.5 23.5V28.5H7.5V23.5C9.43 23.5 11 21.93 11 20C11 18.07 9.43 16.5 7.5 16.5V11.5Z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M16 15V25" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="2.4 3" />
    </svg>
  );
}

/** Документы: лист со строками текста и загнутым углом. */
export function DocumentPrintIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
      <path
        d="M11 7.5H23L29 13.5V32.5H11V7.5Z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M23 7.5V13.5H29" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path
        d="M15.5 20H24.5M15.5 25H24.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Фотопечать: кадр с горизонтом и солнцем. */
export function PhotoPrintIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
      <rect x="7.5" y="9.5" width="25" height="21" rx="3" stroke="currentColor" strokeWidth="2.2" />
      <circle cx="15" cy="16.5" r="2.6" stroke="currentColor" strokeWidth="2" />
      <path
        d="M9 26.5L16.5 20L21.5 24L26 21L31 25.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Полароид: снимок с широким белым полем снизу — его ни с чем не спутать. */
export function PolaroidPrintIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
      <rect x="8.5" y="6.5" width="23" height="27" rx="2.5" stroke="currentColor" strokeWidth="2.2" />
      <rect x="12.5" y="10.5" width="15" height="13" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <path d="M13 28.5H21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/** Знак услуги по её виду. Незнакомая услуга получает знак документа. */
export function BonusIcon({ kind, className }: { kind: string; className?: string }) {
  if (kind === "points") return <PointsIcon className={className} />;
  if (kind === "coupon") return <CouponIcon className={className} />;
  if (kind === "polaroid_print") return <PolaroidPrintIcon className={className} />;
  if (kind === "photo_print" || kind === "inkjet_print" || kind === "photo_id") {
    return <PhotoPrintIcon className={className} />;
  }
  return <DocumentPrintIcon className={className} />;
}

/** Русское склонение: 1 лист, 2 листа, 5 листов. */
export function pluralize(count: number, forms: [string, string, string]): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/** Сколько чего: «5 листов», «1 снимок». */
export function describePrints(serviceType: string, count: number): string {
  const forms = BONUS_UNITS[serviceType] ?? ["штука", "штуки", "штук"];
  return `${count} ${pluralize(count, forms)}`;
}

/** «1 бонус», «2 бонуса», «5 бонусов». */
export function pluralBonuses(count: number): string {
  return pluralize(count, ["бонус", "бонуса", "бонусов"]);
}

/** «1 купон», «2 купона», «5 купонов». */
export function pluralCoupons(count: number): string {
  return pluralize(count, ["купон", "купона", "купонов"]);
}
